import CustomModal from "../../../components/CustomComponents/modal/customModal";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";

const formatDateTime = (val) => {
  if (!val) return "—";
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const getStatusStyle = (status, themeColor) => {
  switch (status) {
    case "Completed":
      return { bg: "rgba(25, 135, 84, 0.15)", color: "#28a745", icon: "fa-solid fa-circle-check" };
    case "Failed":
      return { bg: "rgba(220, 53, 69, 0.15)", color: "#dc3545", icon: "fa-solid fa-circle-xmark" };
    case "Running":
      return { bg: `${themeColor}22`, color: themeColor, icon: "fa-solid fa-spinner fa-spin" };
    case "Queued":
      return { bg: "rgba(255, 193, 7, 0.15)", color: "#ffc107", icon: "fa-solid fa-clock" };
    case "Cancelled":
      return { bg: "rgba(108, 117, 125, 0.2)", color: "#adb5bd", icon: "fa-solid fa-ban" };
    default:
      return { bg: "rgba(108, 117, 125, 0.2)", color: "#adb5bd", icon: "fa-solid fa-circle" };
  }
};

function RunStat({ label, value, color, isDarkMode, panelBorder }) {
  return (
    <div
      className="text-center flex-fill"
      style={{
        padding: "8px 6px",
        borderRadius: "8px",
        backgroundColor: isDarkMode ? "#2d3436" : "#fff",
        border: `1px solid ${panelBorder}`,
        minWidth: "72px",
      }}
    >
      <div className="small text-uppercase fw-semibold" style={{ color, fontSize: "0.65rem", letterSpacing: "0.04em" }}>
        {label}
      </div>
      <div className="fw-bold" style={{ fontSize: "1.1rem", lineHeight: 1.2 }}>
        {value}
      </div>
    </div>
  );
}

function RunHistoryRow({ run, themeColor, isDarkMode, panelBorder, mutedText }) {
  const statusStyle = getStatusStyle(run.status, themeColor);

  return (
    <div
      className="rounded-3 p-3"
      style={{
        backgroundColor: isDarkMode ? "#2d3436" : "#fff",
        border: `1px solid ${panelBorder}`,
      }}
    >
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <span
          className="badge rounded-pill d-inline-flex align-items-center gap-2 px-3 py-2"
          style={{
            backgroundColor: statusStyle.bg,
            color: statusStyle.color,
            fontSize: "0.8rem",
            fontWeight: 600,
          }}
        >
          <i className={statusStyle.icon} />
          {run.status || "—"}
        </span>
        <div className="small" style={{ color: mutedText }}>
          <i className="fa-regular fa-clock me-1" />
          {formatDateTime(run.createdDateTime)}
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2">
        <RunStat
          label="Sent"
          value={run.sentCount ?? 0}
          color="#28a745"
          isDarkMode={isDarkMode}
          panelBorder={panelBorder}
        />
        <RunStat
          label="Failed"
          value={run.failedCount ?? 0}
          color="#dc3545"
          isDarkMode={isDarkMode}
          panelBorder={panelBorder}
        />
        <RunStat
          label="Skipped"
          value={run.skippedCount ?? 0}
          color={mutedText}
          isDarkMode={isDarkMode}
          panelBorder={panelBorder}
        />
        {(run.totalRecipients ?? 0) > 0 && (
          <RunStat
            label="Total"
            value={run.totalRecipients}
            color={themeColor}
            isDarkMode={isDarkMode}
            panelBorder={panelBorder}
          />
        )}
      </div>

      {run.completedDateTime && run.status === "Completed" && (
        <div className="small mt-2" style={{ color: mutedText }}>
          Finished {formatDateTime(run.completedDateTime)}
        </div>
      )}
    </div>
  );
}

function CampaignRunHistoryModal({ isOpen, onClose, campaign, runs, loading }) {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const panelBg = isDarkMode ? "#1e2124" : "#eef1f4";
  const panelBorder = isDarkMode ? "#495057" : "#dee2e6";
  const mutedText = isDarkMode ? "#adb5bd" : "#6c757d";

  const campaignName = campaign?.name ?? "Campaign";
  const totalSent = runs.reduce((sum, r) => sum + (r.sentCount ?? 0), 0);
  const totalFailed = runs.reduce((sum, r) => sum + (r.failedCount ?? 0), 0);

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Run history"
      subtitle={campaignName}
      showButton={true}
      showSubmitButton={false}
      isCentered={true}
      dialogMaxWidth="560px"
      maxHeight="80vh"
    >
      {loading ? (
        <div
          className="d-flex align-items-center justify-content-center gap-2 py-4"
          style={{ color: mutedText }}
        >
          <i className="fa fa-spinner fa-spin" />
          Loading runs…
        </div>
      ) : runs.length === 0 ? (
        <div
          className="text-center py-4 rounded-3"
          style={{
            backgroundColor: panelBg,
            border: `1px solid ${panelBorder}`,
            color: mutedText,
          }}
        >
          <i className="fa-solid fa-clock-rotate-left fa-2x mb-3 d-block" style={{ opacity: 0.5 }} />
          <p className="mb-0 fw-semibold">No runs yet</p>
          <p className="small mb-0 mt-1">Run this campaign to see history here.</p>
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          <div
            className="d-flex flex-wrap align-items-center justify-content-between gap-2 rounded-3 px-3 py-2"
            style={{
              backgroundColor: panelBg,
              border: `1px solid ${panelBorder}`,
            }}
          >
            <span className="small fw-semibold">
              {runs.length} {runs.length === 1 ? "run" : "runs"}
            </span>
            <span className="small" style={{ color: mutedText }}>
              <span style={{ color: "#28a745" }}>{totalSent} sent</span>
              {totalFailed > 0 && (
                <>
                  {" · "}
                  <span style={{ color: "#dc3545" }}>{totalFailed} failed</span>
                </>
              )}
            </span>
          </div>

          <div
            className="d-flex flex-column gap-2"
            style={{ maxHeight: "50vh", overflowY: "auto", paddingRight: "2px" }}
          >
            {runs.map((run) => (
              <RunHistoryRow
                key={run.campaignRun_Guid}
                run={run}
                themeColor={themeColor}
                isDarkMode={isDarkMode}
                panelBorder={panelBorder}
                mutedText={mutedText}
              />
            ))}
          </div>
        </div>
      )}
    </CustomModal>
  );
}

export default CampaignRunHistoryModal;
